<template>

	<AppTable
		ref="views-table"
		:table-headings="tableHeadings"
		:title="getString('views')"
		:total-items="parsedViews.length"
		:items="currentShownViews"
		:filters="filters"
		:main-color="'#0752A3'"
		@changed-page="v=>currentPage = v"
		@changed-page-size="v=>pageSize = v"
		@changed-sort="changeSort"
		@changed-filter="changeFilter"
		allow-download
		@export="exportViews"
		:info-button="getString('info_table_views', [userStore.menuWord])"
		class="mb-30"
	/>
	
	<AppTable
		ref="translations-table"
		:table-headings="tableHeadingsTranslations"
		:title="getString('translations')"
		:total-items="parsedTranslations.length"
		:items="currentShownTranslations"
		:filters="filtersTranslations"
		:main-color="'#0752A3'"
		@changed-page="v=>translationsCurrentPage = v"
		@changed-page-size="v=>translationsPageSize = v"
		@changed-sort="changeSortTranslations"
		@changed-filter="changeFilterTranslations"
		allow-download
		@export="exportTranslations"
		:app-select-values="['25']"
		:info-button="getString('info_table_translations', [userStore.menuWord])"
		class="mb-30"
	/>

</template>

<script>
import {useLanguageStore, useScriptStore, useNotificationStore} from "@/shared/stores";
import {useAnalyticsStore, useUnchangingStore} from "@/console-new/stores";
import AppTable from "@/shared/components/AppTable.vue";
import {formatDateOnly} from "@/shared/helpers/dates";
import {useUserStore} from "@/user/stores";

export default {
	setup() {
		const languageStore = useLanguageStore();
		const analyticsStore = useAnalyticsStore();
		const userStore = useUserStore();
		const scriptStore = useScriptStore();
		const notificationStore = useNotificationStore();
		const unchangingStore = useUnchangingStore();
		return {analyticsStore, getString: languageStore.getString, userStore, scriptStore, showNotification: notificationStore.showNotification, unchangingStore};
	},
	name: "ViewsTablesAggregated",
	components: {AppTable},
	props: {
		dates: {
			type: Object,
			required: true,
			validator: (value) => {
				return value.hasOwnProperty("start") && value.hasOwnProperty("end") && value.hasOwnProperty("previousStart") && value.hasOwnProperty("previousEnd") && value.hasOwnProperty("timeframeIndex");
			}
		},
	},
	data() {
		return {
			tableHeadings: [
				{
					text: this.getString('date'),
					fieldName: 'label_full',
					sortable: true,
					cols: 2,
				},
				{
					text: this.getString('views'),
					fieldName: 'views',
					sortable: true,
					cols: 1,
				},
			],
			filters: [
				{
					type: "quantity",
					key: "views",
					text: this.getString('views'),
					active: false,
					value: null,
					activeItem: 0,
					finalValue: '',
				},
			],
			pageSize: 10,
			currentPage: 1,
			sortField: "label_full",
			sortAsc: null,
			selectedFilters: null,
			// Translations table data
			tableHeadingsTranslations: [
				{
					text: this.getString('language_code'),
					fieldName: 'language_code',
					sortable: true,
					cols: 1,
				},
				{
					text: this.getString('language'),
					fieldName: 'language_name',
					sortable: true,
					cols: 2,
				},
				{
					text: this.getString('translations'),
					fieldName: 'translations',
					sortable: true,
					cols: 1,
				},
			],
			filtersTranslations: [
				{
					type: "quantity",
					key: "translations",
					text: this.getString('translations'),
					active: false,
					value: null,
					activeItem: 0,
					finalValue: '',
				},
			],
			translationsPageSize: 25,
			translationsCurrentPage: 1,
			translationsSortField: "translations",
			translationsSortAsc: false,
			translationsSelectedFilters: null
		};
	},
	computed: {
		parsedViews() {
			const xaxisTimes = this.analyticsStore.xaxisTimesAggregated(this.dates);
			const datesDivision = [];
			
			// Initialize datesDivision with proper structure
			xaxisTimes.forEach(timePoint => {
				datesDivision.push({
					label_full: timePoint.label_full || timePoint.label,
					rawDate: timePoint.rawDate,
					views: 0
				});
			});

			// Process aggregated visits data - use visits_menu like ViewsChartsAggregated does
			if (this.analyticsStore.viewsAggregated.visits_menu) {
				const bucketColumn = this.analyticsStore.viewsAggregated.bucketColumn || 'day_bucket';
				
				this.analyticsStore.viewsAggregated.visits_menu.forEach((row, rowIndex) => {
					const bucketDate = new Date(row[bucketColumn]);
					
					// Align hours for non-hourly data
					if (xaxisTimes.length > 0 && bucketColumn !== 'hour_bucket') {
						bucketDate.setHours(xaxisTimes[0].rawDate.getHours(), xaxisTimes[0].rawDate.getMinutes(), 0, 0);
					}
					
					const mockView = { date: bucketDate };
					const index = this.analyticsStore.getDatesIndex(this.dates, xaxisTimes, mockView, false);
					
					if (index >= 0 && index < datesDivision.length) {
						datesDivision[index].views += Number(row.visits_count || 0);
					}
				});
			}

			let filteredDates = this.filterViews(datesDivision);
			if(this.sortField) {
				if(!this.sortAsc) {
					if(this.sortField === "label_full") {
						filteredDates.sort((b,a) => a.rawDate - b.rawDate);
					} else {
						filteredDates.sort((b,a) => a[this.sortField] - b[this.sortField]);
					}
				} else {
					if(this.sortField === "label_full") {
						filteredDates.sort((a, b) => a.rawDate - b.rawDate);
					} else {
						filteredDates.sort((a, b) => a[this.sortField] - b[this.sortField]);
					}
				}
			}
			return filteredDates;
		},
		currentShownViews() {
			return this.parsedViews.slice(
				(this.currentPage - 1) * this.pageSize,
				this.currentPage * this.pageSize
			);
		},
		languageMap() {
			// Create a map from language codes to language objects from unchangingStore
			const map = {};
			if (this.unchangingStore.languages) {
				this.unchangingStore.languages.forEach(lang => {
					map[lang.language_code] = lang;
				});
			}
			return map;
		},
		parsedTranslations() {
			const translationsArray = [];
			
			if (this.analyticsStore.viewsAggregated.translations) {
				const bucketColumn = this.analyticsStore.viewsAggregated.bucketColumn || 'day_bucket';
				const languageTotals = {};
				
				// Filter translations data by date range
				const filteredData = this.analyticsStore.viewsAggregated.translations.filter(row => {
					const date = new Date(row[bucketColumn]);
					if (bucketColumn === 'hour_bucket') {
						const adjustedEndDate = new Date(this.dates.end);
						adjustedEndDate.setTime(adjustedEndDate.getTime() + 3600 * 1000);
						return date >= this.dates.start && date <= adjustedEndDate;
					}
					return date >= this.dates.start && date <= this.dates.end;
				});
				
				// Aggregate translations by language
				filteredData.forEach(row => {
					const lang = row.language;
					if (!languageTotals[lang]) {
						languageTotals[lang] = 0;
					}
					languageTotals[lang] += Number(row.translation_count || 0);
				});
				
				// Convert to array format
				Object.entries(languageTotals).forEach(([lang, count]) => {
					const languageObj = this.languageMap[lang];
					translationsArray.push({
						language_code: lang.toUpperCase(),
						language_name: languageObj ? languageObj.language_nativeName : lang.toUpperCase(),
						translations: count
					});
				});
			}
			
			// Apply filters
			let filteredTranslations = this.filterTranslations(translationsArray);
			
			// Apply sorting
			if (this.translationsSortField) {
				if (!this.translationsSortAsc) {
					if (this.translationsSortField === "language_code" || this.translationsSortField === "language_name") {
						filteredTranslations.sort((b, a) => a[this.translationsSortField].localeCompare(b[this.translationsSortField]));
					} else {
						filteredTranslations.sort((b, a) => a[this.translationsSortField] - b[this.translationsSortField]);
					}
				} else {
					if (this.translationsSortField === "language_code" || this.translationsSortField === "language_name") {
						filteredTranslations.sort((a, b) => a[this.translationsSortField].localeCompare(b[this.translationsSortField]));
					} else {
						filteredTranslations.sort((a, b) => a[this.translationsSortField] - b[this.translationsSortField]);
					}
				}
			}
			
			return filteredTranslations;
		},
		currentShownTranslations() {
			return this.parsedTranslations.slice(
				(this.translationsCurrentPage - 1) * this.translationsPageSize,
				this.translationsCurrentPage * this.translationsPageSize
			);
		},
	},
	methods: {
		filterViews(viewsArray) {
			if(!viewsArray || !viewsArray.length || !this.selectedFilters || !Object.values(this.selectedFilters).length) {
				return viewsArray;
			}
			for(let filterField in this.selectedFilters) {
				let filterValue = this.selectedFilters[filterField];
				if(filterValue && ['views'].includes(filterField)) {
					if(filterValue.startsWith('<')) {
						let finalValue = Number(filterValue.replace('<', ''));
						viewsArray = viewsArray.filter(b=>b[filterField] < finalValue);
					} else if(filterValue.startsWith('>')) {
						let finalValue = Number(filterValue.replace('>', ''));
						viewsArray = viewsArray.filter(b=>b[filterField] > finalValue);
					} else {
						let finalValue = Number(filterValue);
						viewsArray = viewsArray.filter(b=>b[filterField] === finalValue);
					}
				}
			}
			return viewsArray;
		},
		changeSort(newSortField, newSortAsc) {
			this.currentPage = 1;
			this.sortField = newSortField;
			this.sortAsc = !!newSortAsc;
			this.$refs['views-table'].$refs['pagination-btns'].activePage = 1;
		},
		changeFilter(newFilters) {
			this.currentPage = 1;
			this.selectedFilters = newFilters;
			this.$refs['views-table'].$refs['pagination-btns'].activePage = 1;
		},
		exportViews(expExtension) {
			const dateKey = this.getString('date');
			const viewsKey = this.getString('views');
			const data = this.parsedViews.map(p => {
				const result = {};
				result[dateKey] = p.label_full;
				result[viewsKey] = Number(p.views.toFixed(2)).toLocaleString(["it-IT"]);
				return result;
			});
			const startDate = formatDateOnly(this.dates.start);
			const endDate = formatDateOnly(this.dates.end);
			const businessNameNorm = this.userStore.currBusiness.name.toLowerCase().replace(/[^a-zA-Z0-9]/g, "");
			const fileName = businessNameNorm+"_"+this.getString("views").toLowerCase()+"_"+this.getString("from").toLowerCase()+"_"+startDate+"_"+this.getString("to").toLowerCase()+"_"+endDate;

			if(data.length > 0){
				this.scriptStore.loadScript('xlsx', () => {
					const worksheet = window.XLSX.utils.json_to_sheet(data);
					const workbook = window.XLSX.utils.book_new();
					window.XLSX.utils.book_append_sheet(workbook, worksheet, this.getString("views"));

					window.XLSX.utils.sheet_add_aoa(worksheet, [[dateKey, viewsKey]], {origin: "A1"});

					if (expExtension === "xlsx") {
						window.XLSX.writeFile(workbook, fileName + ".xlsx");
					} else {
						const wbout = window.XLSX.utils.sheet_to_csv(worksheet);
						const blob = new Blob([wbout], {type:"application/octet-stream"});
						const url = URL.createObjectURL(blob);
						const a = document.createElement("a");
						a.href = url;
						a.download = fileName + ".csv";
						a.click();
						URL.revokeObjectURL(url);
					}
				});
			} else {
				this.showNotification('generic-warning');
			}
		},
		// Translations table methods
		filterTranslations(translationsArray) {
			if(!translationsArray || !translationsArray.length || !this.translationsSelectedFilters || !Object.values(this.translationsSelectedFilters).length) {
				return translationsArray;
			}
			for(let filterField in this.translationsSelectedFilters) {
				let filterValue = this.translationsSelectedFilters[filterField];
				if(filterValue && ['translations'].includes(filterField)) {
					if(filterValue.startsWith('<')) {
						let finalValue = Number(filterValue.replace('<', ''));
						translationsArray = translationsArray.filter(t=>t[filterField] < finalValue);
					} else if(filterValue.startsWith('>')) {
						let finalValue = Number(filterValue.replace('>', ''));
						translationsArray = translationsArray.filter(t=>t[filterField] > finalValue);
					} else {
						let finalValue = Number(filterValue);
						translationsArray = translationsArray.filter(t=>t[filterField] === finalValue);
					}
				}
			}
			return translationsArray;
		},
		changeSortTranslations(newSortField, newSortAsc) {
			this.translationsCurrentPage = 1;
			this.translationsSortField = newSortField;
			this.translationsSortAsc = !!newSortAsc;
			if (this.$refs['translations-table']?.$refs['pagination-btns']) {
				this.$refs['translations-table'].$refs['pagination-btns'].activePage = 1;
			}
		},
		changeFilterTranslations(newFilters) {
			this.translationsCurrentPage = 1;
			this.translationsSelectedFilters = newFilters;
			if (this.$refs['translations-table']?.$refs['pagination-btns']) {
				this.$refs['translations-table'].$refs['pagination-btns'].activePage = 1;
			}
		},
		exportTranslations(expExtension) {
			const languageCodeKey = this.getString('language_code');
			const languageKey = this.getString('language');
			const translationsKey = this.getString('translations');
			const data = this.parsedTranslations.map(t => {
				const result = {};
				result[languageCodeKey] = t.language_code;
				result[languageKey] = t.language_name;
				result[translationsKey] = Number(t.translations).toLocaleString(["it-IT"]);
				return result;
			});
			const startDate = formatDateOnly(this.dates.start);
			const endDate = formatDateOnly(this.dates.end);
			const businessNameNorm = this.userStore.currBusiness.name.toLowerCase().replace(/[^a-zA-Z0-9]/g, "");
			const fileName = businessNameNorm+"_"+this.getString("translations").toLowerCase()+"_"+this.getString("from").toLowerCase()+"_"+startDate+"_"+this.getString("to").toLowerCase()+"_"+endDate;

			if(data.length > 0){
				this.scriptStore.loadScript('xlsx', () => {
					const worksheet = window.XLSX.utils.json_to_sheet(data);
					const workbook = window.XLSX.utils.book_new();
					window.XLSX.utils.book_append_sheet(workbook, worksheet, this.getString("translations"));

					window.XLSX.utils.sheet_add_aoa(worksheet, [[languageCodeKey, languageKey, translationsKey]], {origin: "A1"});

					if (expExtension === "xlsx") {
						window.XLSX.writeFile(workbook, fileName + ".xlsx");
					} else {
						const wbout = window.XLSX.utils.sheet_to_csv(worksheet);
						const blob = new Blob([wbout], {type:"application/octet-stream"});
						const url = URL.createObjectURL(blob);
						const a = document.createElement("a");
						a.href = url;
						a.download = fileName + ".csv";
						a.click();
						URL.revokeObjectURL(url);
					}
				});
			} else {
				this.showNotification('generic-warning');
			}
		},
	},
}
</script>